import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { FileText, Mail, Download, Send, AlertTriangle, Check, Search, Filter, Clock, Eye, X, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { getBookCover } from '@/lib/assets';

const schoolSelections = [
  { id: 1, school: "Orchid Primary", shortName: 'OPS', level: 'Pri', cluster: 'Central', poNumber: 'PO-OPS-CDS-C1-2028', books: 91, amount: 2847.50, budget: 3000, status: 'submitted', submittedDate: '12 Sep 2026' },
  { id: 2, school: 'ACS (Independent)', shortName: 'ACS(I)', level: 'Sec', cluster: 'Central', poNumber: 'PO-ACSI-CDS-C1-2028', books: 95, amount: 2980.00, budget: 3000, status: 'submitted', submittedDate: '10 Sep 2026' },
  { id: 3, school: 'Nanyang Primary', shortName: 'NYP', level: 'Pri', cluster: 'West', poNumber: 'PO-NYP-CDS-C1-2028', books: 82, amount: 2450.00, budget: 3000, status: 'submitted', submittedDate: '14 Sep 2026' },
  { id: 4, school: 'Victoria Junior College', shortName: 'VJC', level: 'Pre-U', cluster: 'Central', poNumber: 'PO-VJC-CDS-C1-2028', books: 78, amount: 2320.00, budget: 3000, status: 'submitted', submittedDate: '11 Sep 2026' },
  { id: 5, school: 'Bedok Green Secondary', shortName: 'BGS', level: 'Sec', cluster: 'East', poNumber: 'PO-BGS-CDS-C1-2028', books: 83, amount: 2320.00, budget: 3000, status: 'draft', submittedDate: null },
  { id: 6, school: 'Jurong East Primary', shortName: 'JEP', level: 'Pri', cluster: 'West', poNumber: 'PO-JEP-CDS-C1-2028', books: 65, amount: 1890.00, budget: 3000, status: 'draft', submittedDate: null },
  { id: 7, school: 'Tampines Primary', shortName: 'TPS', level: 'Pri', cluster: 'East', poNumber: null, books: 0, amount: 0, budget: 3000, status: 'not_started', submittedDate: null },
  { id: 8, school: 'Woodlands Secondary', shortName: 'WLS', level: 'Sec', cluster: 'North', poNumber: null, books: 0, amount: 0, budget: 3000, status: 'not_started', submittedDate: null },
];

// Sample order details for schools
const schoolOrderDetails = {
  'OPS': [
    { title: 'The Wonders of Coding', author: 'Sarah Chen', qty: 1, price: 18.50, category: 'NF EL' },
    { title: 'Climate Change & You', author: 'David Atkins', qty: 2, price: 14.20, category: 'NF EL' },
    { title: 'Between Two Worlds', author: 'Amelia Tan', qty: 2, price: 16.80, category: 'Fiction EL' },
    { title: 'The Last Library', author: 'Priya Nair', qty: 1, price: 15.90, category: 'Fiction EL' },
    { title: 'Singapore Stories Vol 3', author: 'Various', qty: 1, price: 22.50, category: 'NF EL' },
    { title: 'Code Red', author: 'Marcus Wu', qty: 1, price: 17.20, category: 'Fiction EL' },
  ],
  'ACS(I)': [
    { title: 'The Wonders of Coding', author: 'Sarah Chen', qty: 2, price: 18.50, category: 'NF EL' },
    { title: 'Climate Change & You', author: 'David Atkins', qty: 1, price: 14.20, category: 'NF EL' },
    { title: 'Daughters of Singapore', author: 'Mei Lin Goh', qty: 2, price: 16.50, category: 'Fiction EL' },
    { title: 'The Memory Keeper', author: 'Siti Rahimah', qty: 1, price: 16.80, category: 'Fiction EL' },
    { title: 'Sound of Silence', author: 'Ravi Menon', qty: 1, price: 15.60, category: 'Fiction EL' },
  ],
  'NYP': [
    { title: 'Singapore Stories Vol 3', author: 'Various', qty: 2, price: 22.50, category: 'NF EL' },
    { title: 'The Wonders of Coding', author: 'Sarah Chen', qty: 1, price: 18.50, category: 'NF EL' },
    { title: 'Climate Change & You', author: 'David Atkins', qty: 1, price: 14.20, category: 'NF EL' },
  ],
  'VJC': [
    { title: 'The Last Library', author: 'Priya Nair', qty: 2, price: 15.90, category: 'Fiction EL' },
    { title: 'Code Red', author: 'Marcus Wu', qty: 2, price: 17.20, category: 'Fiction EL' },
    { title: 'Daughters of Singapore', author: 'Mei Lin Goh', qty: 1, price: 16.50, category: 'Fiction EL' },
  ]
};

const reminderLog = [
  { id: 1, type: '7-day auto-reminder', date: '08 Sep 2026', recipients: 51, actedCount: 24, actedPercent: 47, status: 'sent' },
  { id: 2, type: '3-day manual', date: '12 Sep 2026', recipients: 27, actedCount: 12, actedPercent: 44, status: 'sent' },
  { id: 3, type: 'Final 1-day', date: '14 Sep 2026', recipients: 15, actedCount: 0, actedPercent: 0, status: 'pending' },
];

const supplierPOs = [
  { id: 1, supplier: 'OpenTrolley', poNumber: 'PO-OT-CDS-C1-2028', titles: 38, books: 4820, amount: 78246.00, status: 'ready' },
  { id: 2, supplier: 'Times Books', poNumber: 'PO-TB-CDS-C1-2028', titles: 22, books: 1240, amount: 18600.00, status: 'ready' },
  { id: 3, supplier: 'Popular Books', poNumber: 'PO-PB-CDS-C1-2028', titles: 15, books: 980, amount: 14896.00, status: 'ready' },
  { id: 4, supplier: 'Grassroots', poNumber: 'PO-GR-CDS-C1-2028', titles: 12, books: 680, amount: 9520.00, status: 'ready' },
];

const collatedData = [
  { title: 'The Wonders of Coding', supplier: 'OpenTrolley', unitPrice: 18.50, totalQty: 287, totalAmount: 5309.50 },
  { title: 'Climate Change & You', supplier: 'OpenTrolley', unitPrice: 14.20, totalQty: 312, totalAmount: 4430.40 },
  { title: 'Singapore Stories Vol 3', supplier: 'OpenTrolley', unitPrice: 22.50, totalQty: 287, totalAmount: 6457.50 },
  { title: 'Between Two Worlds', supplier: 'Times Books', unitPrice: 16.50, totalQty: 198, totalAmount: 3267.00 },
  { title: 'The Last Library', supplier: 'Times Books', unitPrice: 15.90, totalQty: 156, totalAmount: 2480.40 },
  { title: 'Semangat!', supplier: 'Grassroots', unitPrice: 16.80, totalQty: 145, totalAmount: 2436.00 },
];

export default function CivicaPOCollation() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const filteredSchools = schoolSelections.filter(school => {
    const matchesSearch = school.school.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         school.shortName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || school.status === statusFilter;
    const matchesLevel = levelFilter === 'all' || school.level === levelFilter;
    return matchesSearch && matchesStatus && matchesLevel;
  });

  const handleSendReminder = () => {
    toast.success('Reminder sent to 39 outstanding schools');
  };

  const handleSendPOs = () => {
    toast.success('POs sent to all 4 suppliers');
  };

  const openSchoolDetails = (school) => {
    setSelectedSchool(school);
    setDetailModalOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
            <FileText className="w-7 h-7 text-[#2E5F8A]" />
            School Selections & Order Collation
          </h1>
          <p className="text-slate-500 mt-1">CDS Cycle 1 2028 | Selection period: 15 Aug – 15 Sep 2026</p>
        </div>
        <Badge className="bg-amber-100 text-amber-700 px-4 py-2">
          <AlertTriangle className="w-4 h-4 mr-2" />
          5 days to deadline
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-emerald-50">
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-emerald-700">248</div>
            <div className="text-sm text-emerald-600">Submitted</div>
            <div className="text-xs text-emerald-500">86.4%</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-amber-700">12</div>
            <div className="text-sm text-amber-600">Draft</div>
            <div className="text-xs text-amber-500">4.2%</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-red-700">27</div>
            <div className="text-sm text-red-600">Not Started</div>
            <div className="text-xs text-red-500">9.4%</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-50">
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-slate-700">287</div>
            <div className="text-sm text-slate-600">Total Schools</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-blue-700">$121K</div>
            <div className="text-sm text-blue-600">Total PO Value</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="status">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="status" data-testid="tab-selection-status">Selection Status (287)</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders">Send Reminders</TabsTrigger>
          <TabsTrigger value="collate" data-testid="tab-collate">Collate Orders</TabsTrigger>
          <TabsTrigger value="generate" data-testid="tab-generate-pos">Generate POs</TabsTrigger>
        </TabsList>

        {/* Selection Status Tab */}
        <TabsContent value="status" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">School Selection Status</CardTitle>
                <CardDescription>Click on a school to view their order details</CardDescription>
              </div>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export Status Report
              </Button>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-6 items-center">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search school..." 
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="Pri">Primary</SelectItem>
                    <SelectItem value="Sec">Secondary</SelectItem>
                    <SelectItem value="Pre-U">Pre-U</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>School</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Cluster</TableHead>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Books</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Budget</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSchools.map((school) => (
                    <TableRow 
                      key={school.id} 
                      className="table-row-striped cursor-pointer hover:bg-slate-100"
                      onClick={() => school.status === 'submitted' && openSchoolDetails(school)}
                    >
                      <TableCell className="font-medium">{school.school}</TableCell>
                      <TableCell>{school.level}</TableCell>
                      <TableCell>{school.cluster}</TableCell>
                      <TableCell className="font-mono text-xs">{school.poNumber || '—'}</TableCell>
                      <TableCell>{school.books || '—'}</TableCell>
                      <TableCell>{school.amount > 0 ? `$${school.amount.toLocaleString()}` : '—'}</TableCell>
                      <TableCell>${school.budget.toLocaleString()}</TableCell>
                      <TableCell>
                        {school.status === 'submitted' && (
                          <Badge className="bg-emerald-50 text-emerald-700">✓ Submitted</Badge>
                        )}
                        {school.status === 'draft' && (
                          <Badge className="bg-amber-50 text-amber-700">📝 Draft</Badge>
                        )}
                        {school.status === 'not_started' && (
                          <Badge className="bg-red-50 text-red-700">⏳ Not Started</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {school.status === 'submitted' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openSchoolDetails(school);
                            }}
                            data-testid={`view-school-${school.shortName}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Order
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4 text-center">Showing {filteredSchools.length} of 287 schools</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Reminder Log & Actions</CardTitle>
              <CardDescription>Track reminder history and send new reminders</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mb-6">
                {reminderLog.map((reminder) => (
                  <div key={reminder.id} className={`flex items-center justify-between p-4 rounded-lg ${
                    reminder.status === 'pending' ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'
                  }`}>
                    <div>
                      <p className="font-medium">{reminder.type}</p>
                      <p className="text-sm text-slate-500">{reminder.date} | {reminder.recipients} schools | {reminder.actedCount} acted ({reminder.actedPercent}%)</p>
                    </div>
                    {reminder.status === 'sent' ? (
                      <Badge className="bg-emerald-100 text-emerald-700">✓ Sent</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">
                        <Clock className="w-3 h-3 mr-1" /> Pending
                      </Badge>
                    )}
                  </div>
                ))}
              </div>

              <div className="p-4 border rounded-lg mb-4">
                <h4 className="font-medium mb-2">Outstanding Schools (39)</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-amber-600 font-medium">12 Draft:</span>
                    <span className="text-slate-500 ml-1">Selection started but not submitted</span>
                  </div>
                  <div>
                    <span className="text-red-600 font-medium">27 Not Started:</span>
                    <span className="text-slate-500 ml-1">No activity yet</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button className="bg-[#2E5F8A] hover:bg-[#254B73]" onClick={handleSendReminder} data-testid="send-bulk-reminder">
                  <Mail className="w-4 h-4 mr-2" />
                  Send Reminder to All 39 Outstanding Schools
                </Button>
                <Button variant="outline">
                  <Mail className="w-4 h-4 mr-2" />
                  Send to Draft Only (12)
                </Button>
                <Button variant="outline">
                  <Mail className="w-4 h-4 mr-2" />
                  Send to Not Started Only (27)
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Collate Orders Tab */}
        <TabsContent value="collate" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Collated Order Summary</CardTitle>
              <CardDescription>Aggregated quantities across all 248 submitted schools</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Unit Price</TableHead>
                    <TableHead>Total Qty</TableHead>
                    <TableHead>Total Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {collatedData.map((item, idx) => (
                    <TableRow key={idx} className="table-row-striped">
                      <TableCell className="font-medium">{item.title}</TableCell>
                      <TableCell>{item.supplier}</TableCell>
                      <TableCell>${item.unitPrice.toFixed(2)}</TableCell>
                      <TableCell>{item.totalQty}</TableCell>
                      <TableCell className="font-semibold">${item.totalAmount.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4 text-center">Showing 6 of 194 titles</p>

              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-slate-500">Total Books</div>
                    <div className="text-xl font-bold">7,720</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Total Value</div>
                    <div className="text-xl font-bold">$121,262</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Schools Included</div>
                    <div className="text-xl font-bold">248 / 287</div>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button className="bg-[#2E5F8A] hover:bg-[#254B73]">
                  <Check className="w-4 h-4 mr-2" />
                  Finalize Collation
                </Button>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export Collation Spreadsheet
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Generate POs Tab */}
        <TabsContent value="generate" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Supplier PO Summary</CardTitle>
              <CardDescription>Generate and send purchase orders to suppliers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {supplierPOs.map((po) => (
                  <div key={po.id} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-lg">{po.supplier}</p>
                        <p className="text-sm text-slate-500 font-mono">{po.poNumber}</p>
                        <p className="text-sm text-slate-500">{po.titles} titles | {po.books.toLocaleString()} books</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-xl">${po.amount.toLocaleString()}</p>
                        <Badge className="bg-emerald-50 text-emerald-700 mt-1">Ready to Send</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-blue-900">Total PO Value</p>
                    <p className="text-sm text-blue-700">4 suppliers | 87 titles | 7,720 books</p>
                  </div>
                  <p className="font-bold text-2xl text-blue-900">$121,262</p>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <Button className="bg-[#2E5F8A] hover:bg-[#254B73]" onClick={handleSendPOs} data-testid="send-pos-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Confirm & Send POs to All 4 Suppliers
                </Button>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Download All School PO PDFs (287)
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* School Order Details Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedSchool && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-[#2E5F8A]" />
                  {selectedSchool.school} — Order Details
                </DialogTitle>
                <DialogDescription>
                  PO: {selectedSchool.poNumber} | Submitted: {selectedSchool.submittedDate}
                </DialogDescription>
              </DialogHeader>

              {/* Summary */}
              <div className="grid grid-cols-4 gap-4 my-4">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-lg font-bold">{selectedSchool.books}</p>
                  <p className="text-xs text-slate-500">Books</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-lg font-bold">${selectedSchool.amount.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Total Amount</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-lg font-bold">${selectedSchool.budget.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Budget</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-emerald-700">${(selectedSchool.budget - selectedSchool.amount).toLocaleString()}</p>
                  <p className="text-xs text-emerald-600">Remaining</p>
                </div>
              </div>

              {/* Order Items */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Cover</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Author</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(schoolOrderDetails[selectedSchool.shortName] || []).map((item, idx) => (
                      <TableRow key={idx} className="table-row-striped">
                        <TableCell>
                          <img 
                            src={getBookCover(item.title)} 
                            alt={item.title}
                            className="w-8 h-12 object-cover rounded"
                          />
                        </TableCell>
                        <TableCell className="font-medium">{item.title}</TableCell>
                        <TableCell>{item.author}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{item.category}</Badge></TableCell>
                        <TableCell>{item.qty}</TableCell>
                        <TableCell>${item.price.toFixed(2)}</TableCell>
                        <TableCell className="font-semibold">${(item.price * item.qty).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex gap-2 mt-4">
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Download PO PDF
                </Button>
                <Button variant="outline">
                  <Mail className="w-4 h-4 mr-2" />
                  Email School
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
